import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, switchMap } from 'rxjs';
import { Namespace, batchCheckAssistantScopes$, listAssistant$ } from '@vendasta/protos/ai_assistants/v1alpha1';

export type AssistantKeyInput = { id: string; namespace: Namespace };

@Injectable({ providedIn: 'root' })
export class AssistantScopeService {
  private readonly cache = new Map<string, Observable<readonly string[]>>();
  private readonly namespaceCache = new Map<string, Observable<boolean>>();

  /** Allowed actions for one assistant. Multicasts; fails closed on RPC error. */
  allowedActions$(key: AssistantKeyInput): Observable<readonly string[]> {
    const cacheKey = serializeAssistantKey(key);
    let entry = this.cache.get(cacheKey);
    if (!entry) {
      entry = batchCheckAssistantScopes$({
        assistantKeys: [{ id: key.id, namespace: key.namespace }],
      }).pipe(
        map((res) => res.entries[0]?.allowedActions ?? []),
        catchError(() => of([] as readonly string[])),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
      this.cache.set(cacheKey, entry);
    }
    return entry;
  }

  /** True when WRITE is allowed. Fails closed via allowedActions$: an RPC error yields [] (denied). */
  canWrite$(key: AssistantKeyInput): Observable<boolean> {
    return this.allowedActions$(key).pipe(map((a) => a.includes('WRITE')));
  }

  /** Bridge for namespace-only callers. Fails closed on RPC error (matches canWrite$); falls
   back to true only when the namespace genuinely has no assistants, preserving the pre-RPC
   partner-id-only behavior. See BREW-2566 for the planned replacement. */
  canWriteInNamespace$(namespace: Namespace): Observable<boolean> {
    const cacheKey = serializeNamespace(namespace);
    let entry = this.namespaceCache.get(cacheKey);
    if (!entry) {
      entry = listAssistant$({
        filters: { namespace },
        pagingOptions: { pageSize: 1 },
      }).pipe(
        switchMap((res) => {
          const first = res.assistants[0];
          if (!first?.id || !first.namespace) return of(true);
          return this.canWrite$({ id: first.id, namespace: first.namespace });
        }),
        catchError(() => of(false)),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
      this.namespaceCache.set(cacheKey, entry);
    }
    return entry;
  }
}

function serializeNamespace(ns: Namespace | undefined): string {
  return ns?.partnerNamespace?.partnerId
    ? `partner:${ns.partnerNamespace.partnerId}`
    : ns?.accountGroupNamespace?.accountGroupId
      ? `account:${ns.accountGroupNamespace.accountGroupId}`
      : ns?.systemNamespace
        ? 'system'
        : ns?.globalNamespace
          ? 'global'
          : 'none';
}

function serializeAssistantKey(key: AssistantKeyInput): string {
  return `${key.id}|${serializeNamespace(key.namespace)}`;
}
